import { TableCellComponentProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { Tag } from '@/modules/Tag';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import { useMemo, memo } from 'react';
import { TagColor } from '@/shared/types/tag';

// Función para generar un hash numérico a partir de una cadena
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

// Función para mezclar colores de forma determinística basándose en un seed
const shuffleColorsWithSeed = (colors: TagColor[], seed: number): TagColor[] => {
  const shuffled = [...colors];
  let currentSeed = seed;

  for (let i = shuffled.length - 1; i > 0; i--) {
    // Generar un número pseudo-aleatorio basado en el seed
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor((currentSeed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
};

export const TagsCell = memo(
  ({
    value,
    column,
    state = 'default',
    className,
    onCellFocus,
    stickyLeft = 0,
    isLastLocked = false,
    isRowEditing,
    hasColumnMaxWidthAuto = false,
  }: TableCellComponentProps) => {
    const baseClasses = getTableCellClasses(
      state,
      className,
      column.locked,
      isLastLocked,
      undefined,
      isRowEditing,
      false,
      column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto)
    );
    const cellProps = getTableCellProps(
      column,
      stickyLeft,
      undefined,
      false,
      hasColumnMaxWidthAuto
    );

    const tags = Array.isArray(value) ? value : [value];

    // Obtener los accessors personalizados de la columna
    const labelAccessor = column.tagLabelAccessor || 'label';
    const colorAccessor = column.tagColorAccessor || 'color';
    const useRandomColors = column.randomColors || false;

    // Colores disponibles (referencia estable para useMemo)
    const availableColors = useMemo<TagColor[]>(
      () => ['accent', 'success', 'warning', 'danger', 'info', 'gray'],
      []
    );

    // Memoizar los colores shuffled basándose en el columnId
    const shuffledColors = useMemo<TagColor[]>(() => {
      if (!useRandomColors) return [];

      const seed = hashString(column.id);
      return shuffleColorsWithSeed(availableColors, seed);
    }, [column.id, useRandomColors, availableColors]);

    // Alineamiento del contenido
    const alignClass = getContentAlignClass(column.contentAlign);
    // Columna con ancho definido → contenedor usa 100% y permite wrap; sin width → contenedor usa 100% para alinear bien
    const hasFixedWidth = column.width !== undefined;

    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div
          className={cn(
            'ui-flex ui-h-full ui-w-full ui-items-center',
            alignClass,
            hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden'
          )}
        >
          <div
            className={cn(
              'ui-inline-flex ui-min-w-0 ui-items-center ui-gap-1',
              hasFixedWidth
                ? 'ui-w-full ui-max-w-full ui-flex-wrap ui-content-start'
                : 'ui-flex-nowrap'
            )}
          >
            {tags.map((tag: unknown, index: number) => {
              // Si tag es un objeto, usar los accessors personalizados
              const tagConfig: {
                label: string;
                color: TagColor;
                avatar?: string;
                onClose?: () => void;
                stroke?: boolean;
              } =
                typeof tag === 'object' && tag !== null
                  ? {
                      label:
                        ((tag as Record<string, unknown>)[labelAccessor] as string) ||
                        ((tag as Record<string, unknown>).label as string) ||
                        String(tag),
                      color: (((tag as Record<string, unknown>)[colorAccessor] as string) ||
                        ((tag as Record<string, unknown>).color as string) ||
                        'gray') as TagColor,
                      avatar: (tag as Record<string, unknown>).avatar as string | undefined,
                      onClose: (tag as Record<string, unknown>).onClose as (() => void) | undefined,
                      stroke: (tag as Record<string, unknown>).stroke as boolean | undefined,
                    }
                  : { label: String(tag), color: 'gray' as TagColor };

              // Si randomColors está activo, asignar color aleatorio sin repetición
              if (useRandomColors && shuffledColors.length > 0) {
                // Usar el índice para asignar colores, si hay más tags que colores, repetir el ciclo
                tagConfig.color = shuffledColors[index % shuffledColors.length];
              }

              return (
                <div
                  key={index}
                  className={cn(hasFixedWidth ? 'ui-min-w-0 ui-max-w-full' : 'ui-flex-shrink-0')}
                >
                  <Tag
                    label={tagConfig.label}
                    color={tagConfig.color}
                    avatar={tagConfig.avatar}
                    onClose={tagConfig.onClose}
                    stroke={tagConfig.stroke ?? false}
                    truncateLabel={hasFixedWidth}
                    truncateTooltip={hasFixedWidth}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </td>
    );
  }
);

TagsCell.displayName = 'TagsCell';
